import {
  findKeyAtTime, getSourceRect, getViewportAt, moveViewport,
  resizeViewport, sortKeys, upsertKey, upsertViewportKey, DYNAMIC_ZOOM_DEFAULT, DYNAMIC_ZOOM_MIN,
  DYNAMIC_ZOOM_MAX,
  type DynamicZoomCorner, type DynamicZoomKey, type DynamicZoomState, type DynamicZoomViewport
} from '../render/dynamicZoom';
import { PreviewAudio } from '../render/previewAudio';
import { drawDynamicZoomFrame } from '../render/dynamicZoomBackground';
import { getFrameSize, getStaticFormatAspect, type StaticFraming } from '../render/staticFraming';
import type { StaticExportFormat } from '../types';
import { AdaptivePreviewQuality, getPausedFootageSize } from '../render/adaptivePreviewQuality';
import { EditorDismissGuard, editorValuesEqual } from './editorDismiss';
import { EditorSnapSession, paintSnapGuides, renderSnapGuides, renderSnapsButton, getEditorSnapsEnabled, setEditorSnapsEnabled } from './editorSnapping';

type PlaybackView = 'edit' | 'preview';

export interface DynamicZoomFrameRequest {
  time: number;
  playing: boolean;
  interactive: boolean;
  size: number;
  signal: AbortSignal;
}

interface EditorOptions {
  state: DynamicZoomState;
  duration: number;
  time: number;
  selectedKeyId: string | null;
  clips: HTMLVideoElement[];
  audioClips?: HTMLVideoElement[];
  framing?: StaticFraming;
  onFormatChange?: (format: StaticExportFormat) => void;
  includeSourceAudio?: boolean;
  onAudioChange?: (enabled: boolean) => void;
  renderFrame: (request: DynamicZoomFrameRequest) => Promise<HTMLCanvasElement | null>;
  onTimeChange: (time: number) => void;
  onSelectionChange: (id: string | null) => void;
  onPlaybackChange: (playing: boolean) => void;
  onClose: (apply: boolean) => void;
  /** Host-owned baseline survives modal re-renders (e.g. changing Format). */
  hasChanges?: () => boolean;
  onKeepEditing?: () => void;
}

export function renderDynamicZoomEditorMarkup(): string {
  return `
    <div class="custom-editor-backdrop" id="dz-editor-backdrop" role="presentation">
      <div class="custom-editor-modal dz-editor-modal" role="dialog" aria-modal="true" aria-labelledby="dz-editor-title">
        <div class="custom-editor-header">
          <div><h3 id="dz-editor-title">Edit Animation</h3>
            <p>Each key marks an arrival. Edit shows the moving frame; Preview shows the final shot. Moving or resizing the frame sets the pose reached at the playhead.</p></div>
          <button id="dz-editor-close" class="star-button" type="button" aria-label="Close animation editor">×</button>
        </div>
        <div class="dz-mode-row" role="group" aria-label="Animation mode">
          <button type="button" data-dz-mode="transitions">Transitions</button>
          <button type="button" data-dz-mode="animation">Animation</button>
        </div>
        <div class="custom-editor-body">
          <div class="custom-editor-stage">
            <div class="dz-stage" id="dz-stage">
              <canvas id="dz-stage-canvas" class="dz-stage-canvas" width="512" height="512"></canvas>
              <div id="dz-stage-shade" class="dz-stage-shade" aria-hidden="true" hidden><div id="dz-shade-window" class="dz-shade-window"></div></div>
              ${renderSnapGuides(true)}
              <div class="dz-window" id="dz-window" tabindex="0" role="group" aria-label="Crop area. Drag to move; use corners to resize." hidden>
                <button type="button" class="dz-handle nw" data-dz-corner="nw" aria-label="Resize crop top left"></button>
                <button type="button" class="dz-handle ne" data-dz-corner="ne" aria-label="Resize crop top right"></button>
                <button type="button" class="dz-handle sw" data-dz-corner="sw" aria-label="Resize crop bottom left"></button>
                <button type="button" class="dz-handle se" data-dz-corner="se" aria-label="Resize crop bottom right"></button>
              </div>
            </div>
          </div>
          <div class="dz-timeline-row">
            <div class="dz-playback-buttons" role="group" aria-label="Animation playback">
              <div class="dz-view-switch" role="group" aria-label="View mode">
                <button type="button" id="dz-edit" class="dz-view" data-dz-view="edit" aria-pressed="true">Edit</button>
                <button type="button" id="dz-preview" class="dz-view" data-dz-view="preview" aria-pressed="false">Preview</button>
              </div>
              <button type="button" id="dz-play" class="dz-play dz-playback-toggle" aria-label="Play" title="Play" data-playing="false">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true">
                  <path class="dz-play-glyph" d="M5 3.2Q3.8 2.5 3.8 3.9V12.1Q3.8 13.5 5 12.8L11.9 8.8Q13.2 8 11.9 7.2Z" />
                  <g class="dz-pause-glyph"><rect x="4" y="3" width="2.8" height="10" rx="1.1" /><rect x="9.2" y="3" width="2.8" height="10" rx="1.1" /></g>
                </svg>
              </button>
            </div>
            <div class="dz-timeline-shell">
              <div class="dz-timeline" id="dz-timeline">
                <div id="dz-time-control" class="dz-time-control" role="slider" tabindex="0" aria-label="Animation timeline" aria-valuemin="0"><div class="dz-timeline-track"></div></div>
                <div class="dz-playhead" id="dz-playhead" aria-hidden="true"></div>
                <div id="dz-keys" class="dz-keys"></div>
              </div>
              <div id="dz-key-menu" class="dz-key-menu" role="group" aria-label="Key framing" hidden>
                <button type="button" data-dz-framing="crop">Crop</button>
                <button type="button" data-dz-framing="full">Full</button>
                <button type="button" id="dz-key-delete" aria-label="Delete key" hidden>Delete</button>
              </div>
            </div>
            <span class="dz-time"></span>
          </div>
          <div id="dz-controls" class="custom-editor-controls dz-controls">
            <div id="dz-key-controls" class="dz-key-controls dz-control-card">
              <div class="dz-key-summary"><span id="dz-key-label"></span></div>
              <label id="dz-zoom-row" class="dz-zoom-row">Zoom
                <input id="dz-key-zoom" type="range" min="${DYNAMIC_ZOOM_MIN}" max="${DYNAMIC_ZOOM_MAX}" step="0.01" aria-label="Crop zoom" />
                <output id="dz-key-zoom-value"></output>
              </label>
            </div>
            <div id="dz-speed-row" class="dz-speed-row dz-control-card" role="group" aria-label="Move speed">
              <span class="dz-control-title">Move speed</span>
              <div class="dz-speed-options">
                <button type="button" data-dz-speed="fast">Fast</button>
                <button type="button" data-dz-speed="medium">Medium</button>
                <button type="button" data-dz-speed="slow">Slow</button>
              </div>
            </div>
            <label class="dz-format-label dz-control-card"><span class="dz-control-title">Format</span><select id="dz-format" aria-label="Animation format">
              <option value="1:1">1:1</option><option value="1:1z">1:1 Zoom</option>
              <option value="16:9">16:9</option><option value="9:16">9:16</option><option value="2:3">2:3</option>
            </select></label>
            <p id="dz-mode-hint" class="hint" hidden></p>
            <label id="dz-audio-row" class="source-audio-control" hidden><input id="dz-use-audio" type="checkbox" checked />Use original audio</label>
            <p id="dz-preview-error" class="hint" role="status" hidden></p>
          </div>
        </div>
        <div class="custom-editor-actions">
          <div class="editor-action-tools"><button id="dz-editor-reset" type="button">Reset</button>${renderSnapsButton('dz-snaps', 'dynamicZoom')}</div>
          <div class="custom-editor-confirm-actions">
            <button id="dz-editor-cancel" type="button">Cancel</button>
            <button id="dz-editor-apply" class="primary" type="button">OK</button>
          </div>
        </div>
      </div>
    </div>`;
}

/** Owns the modal DOM so a pointer gesture never rebuilds the application. */
export class DynamicZoomEditor {
  private readonly lifetime = new AbortController();
  private readonly canvas: HTMLCanvasElement;
  private readonly stage: HTMLElement;
  private readonly timeline: HTMLElement;
  private readonly scrubber: HTMLElement;
  private readonly window: HTMLElement;
  private readonly menu: HTMLElement;
  private readonly framing: StaticFraming;
  private readonly dismissGuard: EditorDismissGuard;
  private time: number;
  private selectedId: string | null;
  private playing = false;
  private playbackView: PlaybackView = 'edit';
  private editingKey = false;
  private playbackVersion = 0;
  private readonly audio: PreviewAudio;
  private scrubbing = false;
  private menuTime: number | null = null;
  private playbackRaf: number | null = null;
  private renderRaf: number | null = null;
  private renderBusy = false;
  private renderPending = false;
  private gestureCleanup: (() => void) | null = null;
  private cachedFrame: HTMLCanvasElement | null = null;
  private frameTime = 0;
  private readonly quality = new AdaptivePreviewQuality([384, 512, 640, 768, 960], 640);

  constructor(private readonly root: HTMLElement, private readonly options: EditorOptions) {
    this.canvas = this.node('dz-stage-canvas');
    this.stage = this.node('dz-stage');
    this.timeline = this.node('dz-timeline');
    this.scrubber = this.node('dz-time-control');
    this.window = this.node('dz-window');
    this.menu = this.node('dz-key-menu');
    this.framing = options.framing ?? { format: '1:1', safeRect: { x: 0, y: 0, w: 1, h: 1 }, referenceSize: 1 };
    const aspect = getStaticFormatAspect(this.framing.format);
    const size = getFrameSize(768, aspect);
    this.canvas.width = size.width;
    this.canvas.height = size.height;
    this.stage.style.setProperty('--dz-aspect', String(aspect));
    this.stage.style.aspectRatio = String(aspect);
    this.root.dataset.format = this.framing.format;
    this.node<HTMLSelectElement>('dz-format').value = this.framing.format;
    this.node<HTMLSelectElement>('dz-format').disabled = !options.onFormatChange;
    this.time = clamp(options.time, 0, options.duration);
    this.selectedId = options.selectedKeyId;
    this.audio = new PreviewAudio(options.audioClips ?? options.clips);
    this.audio.setEnabled(options.includeSourceAudio !== false);
    this.node<HTMLInputElement>('dz-use-audio').checked = options.includeSourceAudio !== false;
    const initial = JSON.parse(JSON.stringify({ state: options.state, format: this.framing.format, audio: options.includeSourceAudio !== false }));
    this.dismissGuard = new EditorDismissGuard(root, {
      hasChanges: options.hasChanges ?? (() => !editorValuesEqual(initial, {
        state: options.state, format: this.framing.format, audio: this.node<HTMLInputElement>('dz-use-audio').checked
      })),
      onClose: options.onClose,
      onKeepEditing: options.onKeepEditing,
      beforePrompt: () => { this.gestureCleanup?.(); this.pause(); this.sync(); this.requestFrame(); }
    });
    void this.audio.ready.then(() => { if (!this.lifetime.signal.aborted) this.node('dz-audio-row').hidden = !this.audio.hasAudio; });
    this.root.style.setProperty('--dz-time-chars', String(options.duration.toFixed(2).length * 2 + 4));
    this.bind();
    this.syncKeys();
    this.sync();
    this.requestFrame();
    this.scrubber.focus({ preventScroll: true });
  }

  dispose(): void {
    this.dismissGuard.dispose();
    this.gestureCleanup?.();
    this.pause();
    this.audio.dispose();
    this.lifetime.abort();
    if (this.renderRaf !== null) cancelAnimationFrame(this.renderRaf);
    this.cachedFrame = null;
  }

  private node<T extends HTMLElement>(id: string): T { return this.root.querySelector(`#${id}`)!; }
  private get displayView(): PlaybackView {
    return this.editingKey && !this.playing ? 'edit' : this.playbackView;
  }
  private get key(): DynamicZoomKey | null { return this.options.state.keys.find(k => k.id === this.selectedId) ?? null; }
  private get viewport(): DynamicZoomViewport {
    const key = !this.playing && !this.scrubbing ? this.key : null;
    if (key && Math.abs(key.time - this.time) <= 1 / 60) {
      return key.framing === 'full' ? { x: 0.5, y: 0.5, zoom: 1 } : key;
    }
    return getViewportAt(this.options.state, this.time, this.options.duration);
  }
  private get clock(): HTMLVideoElement | null {
    return this.options.clips.reduce<HTMLVideoElement | null>((best, clip) => !best || clip.duration > best.duration ? clip : best, null);
  }
  private setTime(time: number): void {
    this.time = clamp(time, 0, this.options.duration);
    this.options.onTimeChange(this.time);
  }
  private select(key: DynamicZoomKey | null): void {
    this.selectedId = key?.id ?? null;
    this.options.onSelectionChange(this.selectedId);
  }

  private toggleSnapping(): void {
    setEditorSnapsEnabled('dynamicZoom', !getEditorSnapsEnabled('dynamicZoom'));
    paintSnapGuides(this.stage.querySelector<HTMLElement>('[data-editor-snap-guides]'), null);
    this.sync();
  }

  private syncKeys(): void {
    const host = this.node('dz-keys');
    host.replaceChildren(...sortKeys(this.options.state.keys).map(key => {
      const node = document.createElement('button');
      node.type = 'button';
      node.className = 'dz-key';
      node.dataset.dzKey = key.id;
      return node;
    }));
  }

  private sync(): void {
    const key = this.key;
    this.node('dz-playhead').style.left = `${this.time / this.options.duration * 100}%`;
    this.scrubber.setAttribute('aria-valuemax', String(this.options.duration));
    this.scrubber.setAttribute('aria-valuenow', this.time.toFixed(2));
    this.scrubber.setAttribute('aria-valuetext', `${this.time.toFixed(2)} of ${this.options.duration.toFixed(2)} seconds`);
    this.root.querySelector('.dz-time')!.textContent = `${this.time.toFixed(2)} / ${this.options.duration.toFixed(2)}s`;
    for (const node of this.root.querySelectorAll<HTMLElement>('[data-dz-key]')) {
      const marker = this.options.state.keys.find(k => k.id === node.dataset.dzKey);
      if (!marker) continue;
      node.style.left = `${marker.time / this.options.duration * 100}%`;
      node.classList.toggle('is-selected', marker.id === this.selectedId);
      node.classList.toggle('is-full', marker.framing === 'full');
      node.setAttribute('aria-pressed', String(marker.id === this.selectedId));
      node.setAttribute('aria-label', `${marker.framing === 'full' ? 'Full' : 'Crop'} key at ${marker.time.toFixed(2)} seconds`);
      node.title = `${marker.time.toFixed(2)}s · ${marker.framing === 'full' ? 'Full' : `${marker.zoom.toFixed(2)}x`}`;
    }
    const viewport = this.viewport;
    this.window.hidden = this.displayView === 'preview';
    this.window.classList.toggle('is-editable', this.displayView === 'edit');
    this.window.classList.toggle('is-playing', this.playing);
    this.window.tabIndex = this.displayView === 'edit' ? 0 : -1;
    const rect = getSourceRect(viewport, 1, 1);
    const windowStyle = { left: `${(rect.x + rect.width / 2) * 100}%`, top: `${(rect.y + rect.height / 2) * 100}%`, width: `${rect.width * 100}%`, height: `${rect.height * 100}%` };
    Object.assign(this.window.style, windowStyle);
    Object.assign(this.node('dz-shade-window').style, windowStyle);
    this.node('dz-stage-shade').hidden = this.displayView !== 'edit' || this.playing;
    this.node('dz-key-label').textContent = key && !this.playing
      ? `${key.framing === 'crop' ? 'Crop' : 'Full frame'} · ${key.time.toFixed(2)}s`
      : `Frame · ${this.time.toFixed(2)}s`;
    this.node<HTMLInputElement>('dz-key-zoom').value = String(viewport.zoom);
    this.node('dz-key-zoom-value').textContent = `${viewport.zoom.toFixed(2)}x`;
    for (const button of this.root.querySelectorAll<HTMLButtonElement>('[data-dz-view]')) {
      button.setAttribute('aria-pressed', String(button.dataset.dzView === this.playbackView));
    }
    const playButton = this.node('dz-play');
    this.node('dz-snaps').setAttribute('aria-pressed', String(getEditorSnapsEnabled('dynamicZoom')));
    playButton.dataset.playing = String(this.playing);
    playButton.setAttribute('aria-label', this.playing ? 'Pause' : 'Play');
    playButton.title = this.playing ? 'Pause' : 'Play';
    this.node('dz-speed-row').hidden = this.options.state.mode !== 'transitions';
    this.node('dz-controls').classList.toggle('is-continuous', this.options.state.mode !== 'transitions');
    const editHint = this.editingKey && this.playbackView === 'preview' && !this.playing;
    this.node('dz-mode-hint').hidden = !editHint;
    this.node('dz-mode-hint').textContent = editHint ? 'Editing key. Play resumes Preview.' : '';
    for (const node of this.root.querySelectorAll<HTMLButtonElement>('[data-dz-mode], [data-dz-speed], [data-dz-framing]')) {
      const active = (node.dataset.dzMode !== undefined && node.dataset.dzMode === this.options.state.mode)
        || (node.dataset.dzSpeed !== undefined && node.dataset.dzSpeed === this.options.state.speed)
        || (node.dataset.dzFraming !== undefined && node.dataset.dzFraming === key?.framing);
      node.setAttribute('aria-pressed', String(active));
    }
    this.menu.hidden = this.menuTime === null || this.playing || this.scrubbing;
    this.node('dz-key-delete').hidden = this.menuTime === null || !findKeyAtTime(this.options.state.keys, this.menuTime);
    if (!this.menu.hidden) {
      const width = this.timeline.clientWidth;
      const half = this.menu.offsetWidth / 2;
      this.menu.style.left = `${clamp(this.menuTime! / this.options.duration * width, half, Math.max(half, width - half))}px`;
    }
  }

  private pause(): void {
    this.playbackVersion += 1;
    this.playing = false;
    this.options.onPlaybackChange(false);
    this.options.clips.forEach(clip => clip.pause());
    this.audio.deactivate();
    if (this.playbackRaf !== null) cancelAnimationFrame(this.playbackRaf);
    this.playbackRaf = null;
  }

  private setPlaybackView(view: PlaybackView): void {
    this.playbackView = view;
    this.editingKey = false;
    this.menuTime = null;
    // Switching the camera presentation never seeks/restarts the media clock
    // or audio. Repaint the current cached frame immediately, even when paused.
    this.sync();
    this.paint();
  }

  private togglePlayback(): void {
    if (this.playing) { this.pause(); this.sync(); this.requestFrame(); }
    else void this.play();
  }

  private beginKeyEdit(): void {
    this.pause();
    // Reveal the full working area, without changing the view chosen for Play.
    this.editingKey = true;
  }

  private async play(): Promise<void> {
    this.pause();
    const version = this.playbackVersion;
    this.editingKey = false;
    this.select(null);
    this.menuTime = null;
    if (this.time >= this.options.duration - 1 / 30) this.setTime(0);
    this.playing = true;
    this.audio.activate();
    this.options.onPlaybackChange(true);
    this.sync();
    this.paint();
    try {
      await Promise.all(this.options.clips.map(async clip => {
        clip.currentTime = Math.min(this.time, Math.max(0, clip.duration - 1 / 120));
        if (this.time < clip.duration) await clip.play();
      }));
    } catch {
      if (version !== this.playbackVersion || this.lifetime.signal.aborted) return;
      this.pause();
      this.node('dz-preview-error').hidden = false;
      this.node('dz-preview-error').textContent = 'Could not play the clip. You can still scrub and edit keys.';
      this.sync();
      return;
    }
    if (this.lifetime.signal.aborted || !this.playing || version !== this.playbackVersion) return;
    this.paint();
    let last = performance.now();
    let paintAt = last - 34;
    const tick = (now: number) => {
      this.playbackRaf = null;
      if (!this.playing || this.lifetime.signal.aborted || version !== this.playbackVersion) return;
      const clock = this.clock;
      const next = clock ? clock.currentTime : this.time + Math.max(0, now - last) / 1000;
      last = now;
      if (next >= this.options.duration - 0.005 || clock?.ended) {
        this.setTime(0);
        this.options.clips.forEach(clip => { clip.currentTime = 0; void clip.play().catch(() => {}); });
      } else this.setTime(next);
      this.sync();
      if (now - paintAt >= 1000 / 30) { paintAt = now; this.requestFrame(); }
      this.playbackRaf = requestAnimationFrame(tick);
    };
    this.playbackRaf = requestAnimationFrame(tick);
  }

  /** One running render and one latest request, never a queue of old seeks. */
  private requestFrame(): void {
    if (this.lifetime.signal.aborted) return;
    this.renderPending = true;
    if (this.renderBusy || this.renderRaf !== null) return;
    this.renderRaf = requestAnimationFrame(() => {
      this.renderRaf = null;
      void this.draw();
    });
  }

  private async draw(): Promise<void> {
    if (this.lifetime.signal.aborted || !this.root.isConnected) return;
    this.renderBusy = true;
    this.renderPending = false;
    const time = this.time;
    const interactive = this.playing || this.scrubbing;
    const started = performance.now();
    try {
      const mobile = window.matchMedia('(pointer: coarse)').matches;
      const extraCrop = this.framing.format === '1:1z' ? 1.5 : 1;
      const size = interactive
        ? Math.min(this.quality.size, mobile ? 768 : 960)
        : getPausedFootageSize(768, this.viewport.zoom * extraCrop, mobile ? 1024 : 1536);
      const frame = await this.options.renderFrame({ time, playing: this.playing, interactive, size, signal: this.lifetime.signal });
      if (frame && !this.lifetime.signal.aborted && this.root.isConnected) {
        this.cachedFrame = frame;
        this.frameTime = time;
        this.paint();
        this.node('dz-preview-error').hidden = true;
      }
      if (interactive) this.quality.record(performance.now() - started, mobile ? 768 : 960);
    } catch (error) {
      if (!this.lifetime.signal.aborted) {
        this.node('dz-preview-error').hidden = false;
        this.node('dz-preview-error').textContent = 'Preview is not ready. Try another point on the timeline.';
      }
    } finally {
      this.renderBusy = false;
      if (this.renderPending) this.requestFrame();
    }
  }

  private paint(): void {
    const frame = this.cachedFrame;
    const ctx = this.canvas.getContext('2d');
    if (!frame || !ctx) return;
    const preview = this.displayView === 'preview';
    const viewport = preview ? getViewportAt(this.options.state, this.frameTime, this.options.duration) : { x: 0.5, y: 0.5, zoom: 1 };
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    drawDynamicZoomFrame(ctx, frame, viewport, this.framing, this.options.state.backgroundMotion);
  }

  private gesture(event: PointerEvent, move: (event: PointerEvent) => void, end: (cancelled: boolean) => void): void {
    this.gestureCleanup?.();
    const controller = new AbortController();
    const target = event.currentTarget as HTMLElement;
    try { target.setPointerCapture(event.pointerId); } catch { /* Window listeners cover missing capture. */ }
    const finish = (cancelled: boolean) => {
      controller.abort();
      this.gestureCleanup = null;
      try { target.releasePointerCapture(event.pointerId); } catch { /* Capture may already be released. */ }
      end(cancelled);
    };
    this.gestureCleanup = () => finish(true);
    window.addEventListener('pointermove', e => { if (e.pointerId === event.pointerId) move(e); }, { signal: controller.signal });
    window.addEventListener('pointerup', e => { if (e.pointerId === event.pointerId) finish(false); }, { signal: controller.signal });
    window.addEventListener('pointercancel', e => { if (e.pointerId === event.pointerId) finish(true); }, { signal: controller.signal });
    window.addEventListener('blur', () => finish(true), { signal: controller.signal });
  }

  private timelineTime(clientX: number): number {
    const rect = this.timeline.getBoundingClientRect();
    return clamp((clientX - rect.left) / Math.max(1, rect.width), 0, 1) * this.options.duration;
  }

  private place(framing: 'crop' | 'full'): void {
    this.beginKeyEdit();
    const time = this.menuTime ?? this.time;
    const existing = findKeyAtTime(this.options.state.keys, time);
    const viewport = existing ?? getViewportAt(this.options.state, time, this.options.duration);
    this.setTime(time);
    this.commitViewport({ ...viewport, zoom: framing === 'full' ? 1 : viewport.zoom > 1 ? viewport.zoom : DYNAMIC_ZOOM_DEFAULT });
    this.menuTime = null;
    this.sync();
    this.paint();
    this.requestFrame();
    if (framing === 'crop') this.window.focus({ preventScroll: true });
  }

  private commitViewport(viewport: DynamicZoomViewport): void {
    const previous = this.options.state.keys;
    const result = upsertViewportKey(previous, this.time, viewport, this.options.duration);
    this.options.state.keys = result.keys;
    this.select(result.key);
    this.setTime(result.key.time);
    if (previous.length !== result.keys.length || !previous.some(key => key.id === result.key.id)) this.syncKeys();
  }

  private removeKey(): void {
    this.options.state.keys = this.options.state.keys.filter(k => k.id !== this.selectedId);
    this.select(null);
    this.menuTime = null;
    this.syncKeys(); this.sync(); this.paint();
  }

  private bind(): void {
    const signal = this.lifetime.signal;
    this.node<HTMLInputElement>('dz-use-audio').addEventListener('change', event => {
      const enabled = (event.target as HTMLInputElement).checked;
      this.audio.setEnabled(enabled);
      this.options.onAudioChange?.(enabled);
    }, { signal });
    this.node<HTMLSelectElement>('dz-format').addEventListener('change', event => {
      const format = (event.target as HTMLSelectElement).value;
      if (!['1:1', '1:1z', '16:9', '9:16', '2:3'].includes(format)) return;
      this.gestureCleanup?.();
      this.pause();
      this.options.onFormatChange?.(format as StaticExportFormat);
    }, { signal });
    this.timeline.addEventListener('pointerdown', event => {
      if (event.button !== 0 || !event.isPrimary) return;
      event.preventDefault();
      this.pause();
      this.menuTime = null;
      const marker = (event.target as HTMLElement).closest<HTMLElement>('[data-dz-key]');
      const key = this.options.state.keys.find(k => k.id === marker?.dataset.dzKey) ?? null;
      const wasEditing = this.editingKey;
      this.editingKey = Boolean(key);
      const originalTime = key?.time ?? this.time;
      const startX = event.clientX;
      let moved = false;
      this.select(key ?? findKeyAtTime(this.options.state.keys, this.timelineTime(startX)));
      this.setTime(key ? key.time : this.timelineTime(startX));
      this.scrubbing = true;
      this.sync(); this.paint(); this.requestFrame();
      this.gesture(event, e => {
        moved ||= Math.abs(e.clientX - startX) > 3;
        if (!moved) return;
        this.scrubbing = true;
        this.setTime(key ? originalTime + (e.clientX - startX) / Math.max(1, this.timeline.clientWidth) * this.options.duration : this.timelineTime(e.clientX));
        if (key) key.time = this.time;
        else this.select(findKeyAtTime(this.options.state.keys, this.time));
        this.sync(); this.requestFrame();
      }, cancelled => {
        this.scrubbing = false;
        if (cancelled) { if (key) key.time = originalTime; this.setTime(originalTime); }
        else if (key && moved) this.options.state.keys = upsertKey(this.options.state.keys, key, this.options.duration);
        this.editingKey = cancelled ? wasEditing : Boolean(this.key);
        if (!cancelled) this.menuTime = this.time;
        this.syncKeys(); this.sync(); this.paint(); this.requestFrame();
      });
    }, { signal });

    this.window.addEventListener('pointerdown', event => {
      if (this.displayView !== 'edit' || event.button !== 0 || !event.isPrimary) return;
      event.preventDefault(); event.stopPropagation();
      const original = { ...this.viewport };
      const previousKeys = this.options.state.keys.map(key => ({ ...key }));
      const previousSelection = this.selectedId;
      this.pause(); this.menuTime = null;
      this.select(findKeyAtTime(this.options.state.keys, this.time));
      const rect = this.stage.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const corner = (event.target as HTMLElement).closest<HTMLElement>('[data-dz-corner]')?.dataset.dzCorner as DynamicZoomCorner | undefined;
      const snap = new EditorSnapSession();
      const guides = this.stage.querySelector<HTMLElement>('[data-editor-snap-guides]');
      if (!corner && getEditorSnapsEnabled('dynamicZoom')) paintSnapGuides(guides, null, true);
      this.window.classList.add('is-dragging');
      let edited = false;
      this.gesture(event, e => {
        const dx = (e.clientX - event.clientX) / Math.max(1, rect.width);
        const dy = (e.clientY - event.clientY) / Math.max(1, rect.height);
        let viewport = corner ? resizeViewport(original, corner, dx, dy) : moveViewport(original, dx, dy);
        if (!corner && getEditorSnapsEnabled('dynamicZoom')) {
          const width = rect.width / viewport.zoom, height = rect.height / viewport.zoom;
          const snapped = snap.move({ x: viewport.x * rect.width - width / 2, y: viewport.y * rect.height - height / 2, width, height }, rect, true);
          viewport = { ...viewport, x: (snapped.rect.x + width / 2) / rect.width, y: (snapped.rect.y + height / 2) / rect.height };
          paintSnapGuides(guides, snapped.guides, true);
        } else {
          snap.reset(); paintSnapGuides(guides, null);
        }
        if (!edited && Math.abs(viewport.x - original.x) + Math.abs(viewport.y - original.y) + Math.abs(viewport.zoom - original.zoom) < 0.001) return;
        edited = true;
        this.commitViewport(viewport);
        this.sync();
      }, cancelled => {
        if (cancelled && edited) {
          this.options.state.keys = previousKeys;
          this.select(previousKeys.find(key => key.id === previousSelection) ?? null);
          this.syncKeys();
        }
        this.window.classList.remove('is-dragging');
        paintSnapGuides(guides, null);
        this.sync(); this.requestFrame();
      });
      this.sync(); this.requestFrame();
    }, { signal });

    this.root.addEventListener('click', event => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('button');
      if (!button) {
        if (event.target !== this.root && !(event.target as HTMLElement).closest('#dz-timeline, #dz-key-menu')) { this.menuTime = null; this.sync(); }
        return;
      }
      if (!button.dataset.dzFraming && !button.dataset.dzKey && button.id !== 'dz-key-delete') this.menuTime = null;
      if (button.dataset.dzFraming) this.place(button.dataset.dzFraming === 'full' ? 'full' : 'crop');
      else if (button.dataset.dzMode) { this.options.state.mode = button.dataset.dzMode === 'animation' ? 'animation' : 'transitions'; this.sync(); this.paint(); }
      else if (button.dataset.dzSpeed) { this.options.state.speed = button.dataset.dzSpeed as DynamicZoomState['speed']; this.sync(); this.paint(); }
      else if (button.dataset.dzView) this.setPlaybackView(button.dataset.dzView === 'preview' ? 'preview' : 'edit');
      else if (button.id === 'dz-play') this.togglePlayback();
      else if (button.id === 'dz-snaps') this.toggleSnapping();
      else if (button.id === 'dz-key-delete') {
        this.select(findKeyAtTime(this.options.state.keys, this.menuTime ?? this.time));
        this.removeKey();
      }
      else if (button.id === 'dz-editor-reset') { this.pause(); this.editingKey = false; this.options.state.keys = []; this.select(null); this.setTime(0); this.menuTime = null; this.syncKeys(); this.sync(); this.requestFrame(); }
      else if (button.id === 'dz-editor-apply') this.options.onClose(true);
      else if (button.id === 'dz-editor-close') this.dismissGuard.requestClose();
      else if (button.id === 'dz-editor-cancel') this.options.onClose(false);
    }, { signal });

    this.node<HTMLInputElement>('dz-key-zoom').addEventListener('pointerdown', () => {
      this.beginKeyEdit(); this.menuTime = null;
      this.select(findKeyAtTime(this.options.state.keys, this.time));
      this.sync(); this.paint(); this.requestFrame();
    }, { signal });
    this.node<HTMLInputElement>('dz-key-zoom').addEventListener('input', event => {
      const viewport = { ...this.viewport, zoom: Number((event.target as HTMLInputElement).value) };
      this.beginKeyEdit(); this.menuTime = null;
      this.commitViewport(viewport);
      this.sync(); this.paint();
    }, { signal });

    this.root.addEventListener('keydown', event => {
      const target = event.target as HTMLElement;
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopPropagation();
        if (this.menuTime !== null) { this.menuTime = null; this.sync(); this.scrubber.focus(); }
        else this.dismissGuard.requestClose();
      } else if (target === this.scrubber) {
        const step = event.shiftKey ? 1 : 1 / 30;
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          event.preventDefault(); this.pause(); this.menuTime = null;
          this.setTime(event.key === 'Home' ? 0 : event.key === 'End' ? this.options.duration : this.time + (event.key === 'ArrowRight' ? step : -step));
          this.select(findKeyAtTime(this.options.state.keys, this.time)); this.editingKey = Boolean(this.key); this.sync(); this.requestFrame();
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault(); this.pause(); this.menuTime = this.time; this.sync(); this.menu.querySelector<HTMLButtonElement>('button')?.focus();
        }
      } else if (target === this.window && this.displayView === 'edit' && event.key.startsWith('Arrow')) {
        event.preventDefault();
        const step = event.shiftKey ? 0.02 : 0.005;
        const viewport = moveViewport(this.viewport, event.key === 'ArrowRight' ? step : event.key === 'ArrowLeft' ? -step : 0, event.key === 'ArrowDown' ? step : event.key === 'ArrowUp' ? -step : 0);
        this.pause(); this.menuTime = null; this.commitViewport(viewport);
        this.sync();
      } else if (target.dataset.dzKey && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault(); this.beginKeyEdit();
        const key = this.options.state.keys.find(k => k.id === target.dataset.dzKey)!;
        this.select(key); this.setTime(key.time); this.menuTime = this.time; this.sync(); this.paint(); this.requestFrame();
      } else if (event.key === 'Delete' && target.tagName !== 'INPUT') { event.preventDefault(); this.removeKey(); }
    }, { signal });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { this.pause(); this.sync(); }
    }, { signal });
  }
}

function clamp(value: number, min: number, max: number): number { return Math.max(min, Math.min(max, value)); }
